import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LeadModalProvider } from "@/context/LeadModalContext";
import { Toaster } from "@/components/ui/sonner";
import { PremiumLanding } from "@/pages/PremiumLanding";
import { NotFound } from "@/pages/NotFound";
import { QuizProvider } from "@/context/QuizContext";
import { LANDINGS } from "@/data/landings";
import { initUtm, trackEvent } from "@/lib/track";
import { initMetaPixel, initTikTokPixel } from "@/analytics/pixels";
import { RouteTracker } from "@/components/RouteTracker";
import { GA_MEASUREMENT_ID } from "@/data/content";

const ProfessionalsPage = lazy(() =>
  import("@/pages/ProfessionalsPage").then((m) => ({ default: m.ProfessionalsPage }))
);
const SlimPage = lazy(() =>
  import("@/pages/SlimPage").then((m) => ({ default: m.SlimPage }))
);
const AdminPage = lazy(() =>
  import("@/pages/AdminPage").then((m) => ({ default: m.AdminPage }))
);
const LandingEngine = lazy(() =>
  import("@/components/landing/LandingEngine").then((m) => ({ default: m.LandingEngine }))
);

const Landing = () => <PremiumLanding />;

function App() {
  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const lenis = new Lenis({ duration: reducedMotion ? 0 : 1.15, smoothWheel: !reducedMotion });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    initUtm();

    // Pixels de marketing fora do caminho crítico: carregam após o load
    // da página ou na primeira interação do usuário — o que vier primeiro.
    const initPixels = () => {
      if (window.__pixels_loaded) return;
      window.__pixels_loaded = true;
      initMetaPixel();
      initTikTokPixel();
      if (!window.__landing_viewed) {
        window.__landing_viewed = true;
        trackEvent("landing_view");
      }
    };
    if (document.readyState === "complete") {
      initPixels();
    } else {
      window.addEventListener("load", initPixels, { once: true });
      ["pointerdown", "touchstart", "keydown"].forEach((evt) =>
        window.addEventListener(evt, initPixels, { once: true, passive: true })
      );
    }

    if (GA_MEASUREMENT_ID) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag("js", new Date());
      window.gtag("config", GA_MEASUREMENT_ID);
    }

    const marks = [25, 50, 75, 100];
    const seen = new Set();
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      const pct = Math.round((window.scrollY / total) * 100);
      marks.forEach((m) => {
        if (pct >= m && !seen.has(m)) {
          seen.add(m);
          trackEvent(`scroll_${m}`);
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            trackEvent(e.target.dataset.trackEvent);
            observer.unobserve(e.target);
          }
        }),
      { threshold: 0.3 }
    );
    document.querySelectorAll("[data-track-event]").forEach((el) => observer.observe(el));

    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <LeadModalProvider>
      <QuizProvider>
        <div className="min-h-screen overflow-x-clip bg-white font-body text-slate-900">
          <BrowserRouter>
            <RouteTracker />
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route path="/profissionais" element={<ProfessionalsPage />} />
                <Route path="/modo-canetinha" element={<PremiumLanding slim />} />
                <Route path="/slim" element={<SlimPage />} />
                <Route path="/admin" element={<AdminPage />} />
                {Object.values(LANDINGS).map((l) => (
                  <Route key={l.slug} path={`/${l.slug}`} element={<LandingEngine config={l} />} />
                ))}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </div>
      </QuizProvider>
    </LeadModalProvider>
  );
}

export default App;
